import React from "react";
import * as FiIcons from "react-icons/fi";
import siteData from "../content/siteData.json";

// Tailwind brand color used in SVG (fallback hex)
const PRIMARY = "#001287";

// Map icon names from JSON to react-icons components
const ICONS = {
  FiTarget: FiIcons.FiTarget,
  FiBarChart: FiIcons.FiBarChart,
  FiCpu: FiIcons.FiCpu,
  FiTrendingUp: FiIcons.FiTrendingUp,
  FiUsers: FiIcons.FiUsers,
};

// helpers
const to360 = (deg) => ((deg % 360) + 360) % 360;
const deg2rad = (d) => (d * Math.PI) / 180;

// Smart text wrapping for circular containers
function wrapTextForCircle(str = "", maxCharsPerLine = 12) {
  if (!str) return [""];
  
  // Handle manual line breaks first
  if (str.includes("\n")) {
    return str.split("\n").map(line => line.trim()).filter(Boolean);
  }
  
  const words = str.trim().split(/\s+/);
  
  // If it's short enough for one line, return as is
  if (str.length <= maxCharsPerLine) {
    return [str];
  }
  
  // For longer text, try to balance across 2-3 lines
  if (words.length === 1) {
    // Single long word - try to hyphenate or abbreviate
    if (str.length > maxCharsPerLine * 2) {
      // Try to split compound words
      const parts = str.split(/(?=[A-Z])/);
      if (parts.length > 1) {
        return wrapTextForCircle(parts.join(" "), maxCharsPerLine);
      }
      // As last resort, break the word
      const mid = Math.floor(str.length / 2);
      return [str.substring(0, mid) + "-", str.substring(mid)];
    }
    return [str];
  }
  
  // Multi-word text - balance across lines
  const lines = [];
  let currentLine = "";
  let remainingWords = [...words];
  
  while (remainingWords.length > 0 && lines.length < 3) {
    const word = remainingWords.shift();
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    
    if (testLine.length <= maxCharsPerLine || currentLine === "") {
      currentLine = testLine;
    } else {
      // Current line is full, start new line
      if (currentLine) lines.push(currentLine);
      currentLine = word;
    }
  }
  
  // Add the last line
  if (currentLine) lines.push(currentLine);
  
  // If we still have remaining words, add them to the last line
  if (remainingWords.length > 0) {
    lines[lines.length - 1] += " " + remainingWords.join(" ");
  }
  
  return lines;
}

export default function Flywheel() {
  const { flywheel } = siteData;

  // viewBox geometry (responsive via aspect-square container)
  const W = 560, H = 560;
  const cx = W / 2, cy = H / 2;
  const rTrack = 180;       // dashed circle
  const rNodes = 210;       // node orbit radius
  const rNode = 50;         // node bubble radius
  const rCenter = 74;       // center disk

  // nodes sorted clockwise for consistent arrow flow
  const nodes = (flywheel?.nodes || [])
    .map(n => ({ ...n, angle: to360(n.angle || 0) }))
    .sort((a, b) => a.angle - b.angle);

  // arc path on the track from start->end clockwise
  const arc = (startDeg, endDeg, r = rTrack) => {
    const s = to360(startDeg), e = to360(endDeg);
    let delta = e - s; if (delta <= 0) delta += 360;
    const largeArc = delta > 180 ? 1 : 0;
    const sweep = 1;
    const sx = cx + r * Math.cos(deg2rad(s));
    const sy = cy + r * Math.sin(deg2rad(s));
    const ex = cx + r * Math.cos(deg2rad(e));
    const ey = cy + r * Math.sin(deg2rad(e));
    return `M ${sx} ${sy} A ${r} ${r} 0 ${largeArc} ${sweep} ${ex} ${ey}`;
  };

  return (
    <div className="relative mx-auto max-w-[680px] aspect-square">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-labelledby="fwTitle fwDesc"
        className="w-full h-full"
      >
        <title id="fwTitle">Strategy flywheel</title>
        <desc id="fwDesc">
          Circular diagram with nodes for Growth Strategy, Strategic Measurement, AI in Practice,
          Data in Action, and Fractional Leadership orbiting a center disk labeled Clarity in Decision-Making.
        </desc>

        {/* defs: arrow marker + soft shadow */}
        <defs>
          <marker id="arrow" markerWidth="14" markerHeight="10" refX="12" refY="5" orient="auto">
            <polygon points="0 0, 14 5, 0 10" fill={PRIMARY} />
          </marker>
          <filter id="fwShadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.15" />
          </filter>
        </defs>

        {/* dashed track */}
        <circle
          cx={cx} cy={cy} r={rTrack}
          fill="none" stroke="#E5E7EB" strokeWidth="3" strokeDasharray="12 6"
          vectorEffect="non-scaling-stroke"
        />

        {/* 3 arrow segments (keeping the curved arrows) */}
        {nodes.length >= 2 && (
          <>
            <path d={arc(-120, -40)} fill="none" stroke={PRIMARY} strokeWidth="3" markerEnd="url(#arrow)" vectorEffect="non-scaling-stroke" />
            <path d={arc(0, 80)}     fill="none" stroke={PRIMARY} strokeWidth="3" markerEnd="url(#arrow)" vectorEffect="non-scaling-stroke" />
            <path d={arc(140, 220)}  fill="none" stroke={PRIMARY} strokeWidth="3" markerEnd="url(#arrow)" vectorEffect="non-scaling-stroke" />
          </>
        )}

        {/* center disk */}
        <g filter="url(#fwShadow)">
          <circle cx={cx} cy={cy} r={rCenter} fill={PRIMARY} />
          {wrapTextForCircle(flywheel?.center?.title || "Clarity in\nDecision-Making", 14).map((line, i, arr) => {
            const totalLines = arr.length;
            const startY = cy - ((totalLines - 1) * 9); // Center the text block vertically
            return (
              <text
                key={i}
                x={cx}
                y={startY + i * 18}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="16"
                fontWeight="700"
                fill="#FFFFFF"
              >
                {line}
              </text>
            );
          })}
        </g>

        {/* nodes */}
        {nodes.map((n, i) => {
          const a = deg2rad(n.angle);
          const x = cx + rNodes * Math.cos(a);
          const y = cy + rNodes * Math.sin(a);
          const Icon = ICONS[n.icon] || FiIcons.FiCircle;
          const textLines = wrapTextForCircle(n.title, 11);

          // --- spacing controls (tweak here) ---
          const ICON_SIZE = 24;       // must match <Icon size={...}/>
          const ICON_TOP_OFFSET = 30; // move icon a bit higher (was 28)
          const TEXT_GAP = 12;        // space between icon bottom and first text line
          const NODE_PADDING = 8;     // keep label inside bubble bottom
          // -------------------------------------

          // icon bottom Y position (in SVG coords)
          const iconTopY = y - ICON_TOP_OFFSET;
          const iconBottomY = iconTopY + ICON_SIZE;

          // initial target start line: just under the icon, with a gap
          const lineSpacing = textLines.length > 2 ? 10 : 12;
          const approxTextHeight = (textLines.length - 1) * lineSpacing + 10; // ~line height

          let textStartY = iconBottomY + TEXT_GAP;

          // clamp so the last line doesn't fall outside the node circle
          const maxBottomY = y + rNode - NODE_PADDING;
          if (textStartY + approxTextHeight > maxBottomY) {
            textStartY = maxBottomY - approxTextHeight;
          }

          return (
            <g key={n.key || i} filter="url(#fwShadow)">
              {/* node bubble */}
              <circle
                cx={x} cy={y} r={rNode}
                fill="#FFFFFF" stroke="#BFD0FF" strokeWidth="2"
                vectorEffect="non-scaling-stroke"
              />

              {/* icon near the top */}
              <g transform={`translate(${x - ICON_SIZE / 2}, ${iconTopY})`}>
                <Icon size={ICON_SIZE} color={PRIMARY} />
              </g>

              {/* label inside circle, safely below the icon */}
              {textLines.map((line, idx) => (
                <text
                  key={idx}
                  x={x}
                  y={textStartY + idx * lineSpacing}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize="10"
                  fontWeight="600"
                  fill="#475569"
                  className="text-[10px] md:text-xs font-medium text-secondary-700"
                >
                  {line}
                </text>
              ))}
            </g>
          );
        })}
      </svg>

      {/* Responsive text sizing */}
      <style>{`
        @media (max-width: 480px) {
          text[font-size="10"] { font-size: 9px; }
        }
        @media (min-width: 768px) {
          text[font-size="10"] { font-size: 11px; }
        }
      `}</style>
    </div>
  );
}